import { Card } from '@chakra-ui/react';
import useIsViewer from '@/lib/hooks/use-is-viewer';
import { dateOf, PlannerAction, PlannerScreen } from '@/pages/planner/planner-machine';
import PlanScopeTabs from './plan-scope-tabs';
import EmptyScreen from './empty-screen';
import ChoiceScreen from './choice-screen';
import ModPastScreen from './mod-past-screen';
import CurBrowseScreen from './cur-browse-screen';
import CurCreateScreen from './cur-create-screen';
import CurStructureScreen from './cur-structure-screen';
import CurBuildScreen from './cur-build-screen';
import CurPlansScreen from './cur-plans-screen';
import BuilderScreen from '../builder/builder-screen';
import ViewScreen from '../view/view-screen';
import CurSlotViewScreen from '../view/cur-slot-view-screen';

interface WorkingPanelCardProps {
  screen: PlannerScreen;
  workspaceId: number;
  dispatch: (action: PlannerAction) => void;
}

// Screens that let a user create/modify content. Viewers must never render
// one of these — this is defense-in-depth layer 2 (layer 1 is
// planner-machine.ts's SELECT_DATE case refusing to transition to `choice`
// for a viewer in the first place). If a viewer's `screen` is somehow one of
// these anyway (e.g. a stale dispatch from a race, or a future action this
// guard wasn't updated for), fall back to the empty-state screen rather than
// rendering a write UI.
const WRITE_SCREEN_NAMES = new Set<PlannerScreen['name']>([
  'choice',
  'modPast',
  'curBrowse',
  'curPlans',
  'curCreate',
  'curStructure',
  'curBuild',
  'builder',
]);

// Screen switcher for the Planner's working panel — the right-hand side of
// the layout that reacts to the screen state machine in planner.tsx.
export default function WorkingPanelCard({ screen, workspaceId, dispatch }: WorkingPanelCardProps) {
  const isViewer = useIsViewer();

  if (isViewer && WRITE_SCREEN_NAMES.has(screen.name)) {
    return (
      <Card.Root>
        <Card.Body>
          <EmptyScreen date={dateOf(screen)} />
        </Card.Body>
      </Card.Root>
    );
  }

  const card = (
    <Card.Root>
      <Card.Body>
        {screen.name === 'empty' && <EmptyScreen date={screen.date} />}
        {screen.name === 'choice' && (
          <ChoiceScreen date={screen.date} scope={screen.scope} workspaceId={workspaceId} dispatch={dispatch} />
        )}
        {screen.name === 'modPast' && (
          <ModPastScreen date={screen.date} scope={screen.scope} dispatch={dispatch} />
        )}
        {screen.name === 'curBrowse' && (
          <CurBrowseScreen intent={screen.intent} date={screen.date} scope={screen.scope} dispatch={dispatch} />
        )}
        {screen.name === 'curPlans' && (
          <CurPlansScreen
            curriculumId={screen.curriculumId}
            date={screen.date}
            scope={screen.scope}
            dispatch={dispatch}
          />
        )}
        {screen.name === 'curCreate' && <CurCreateScreen draft={screen.draft} dispatch={dispatch} />}
        {screen.name === 'curStructure' && (
          <CurStructureScreen workspaceId={workspaceId} draft={screen.draft} dispatch={dispatch} />
        )}
        {screen.name === 'curBuild' && (
          <CurBuildScreen curriculumId={screen.curriculumId} dispatch={dispatch} />
        )}
        {screen.name === 'builder' && (
          <BuilderScreen ctx={screen.ctx} initialDraft={screen.initialDraft} dispatch={dispatch} />
        )}
        {screen.name === 'view' && <ViewScreen date={screen.date} scope={screen.scope} dispatch={dispatch} />}
        {screen.name === 'curSlotView' && (
          <CurSlotViewScreen curriculumId={screen.curriculumId} slotId={screen.slotId} dispatch={dispatch} />
        )}
      </Card.Body>
    </Card.Root>
  );

  // The workspace/my-plan tab strip frames the two day-anchored screens.
  // Creation sub-flows (modPast, curriculum picking, the builder) keep the
  // bare card — mid-flow scope switching would silently drop their state.
  if (screen.name === 'view' || screen.name === 'choice') {
    return (
      <PlanScopeTabs
        date={screen.date}
        activeScope={screen.scope}
        isCreating={screen.name === 'choice'}
        dispatch={dispatch}
      >
        {card}
      </PlanScopeTabs>
    );
  }

  return card;
}
